'use client';

import React, { useEffect, useState } from 'react';

import { usePluginConfig } from '../hooks/usePluginConfig';
import { useTagadaContext } from '../providers/TagadaProvider';

interface DebugDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TreeViewProps {
  data: unknown;
  name: string;
  level?: number;
  maxLevel?: number;
}

const safeStringify = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '[object]';
  }
};

const TreeView: React.FC<TreeViewProps> = ({ data, name, level = 0, maxLevel = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);

  if (level > maxLevel) {
    return <span style={{ color: '#666' }}>...</span>;
  }

  if (data === null) {
    return <span style={{ color: '#f59e0b' }}>null</span>;
  }

  if (data === undefined) {
    return <span style={{ color: '#6b7280' }}>undefined</span>;
  }

  if (typeof data === 'string') {
    return <span style={{ color: '#10b981' }}>"{data}"</span>;
  }

  if (typeof data === 'number') {
    return <span style={{ color: '#3b82f6' }}>{data}</span>;
  }

  if (typeof data === 'boolean') {
    return <span style={{ color: '#8b5cf6' }}>{data.toString()}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span style={{ color: '#6b7280' }}>[]</span>;
    }

    return (
      <div style={{ marginLeft: level > 0 ? '16px' : '0' }}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            padding: '0',
            fontSize: '14px',
          }}
        >
          {isExpanded ? '▼' : '▶'} {name} ({data.length} items)
        </button>
        {isExpanded && (
          <div style={{ marginLeft: '16px' }}>
            {data.slice(0, 10).map((item, index) => (
              <TreeView key={index} data={item} name={`[${index}]`} level={level + 1} maxLevel={maxLevel} />
            ))}
            {data.length > 10 && (
              <div style={{ color: '#6b7280', fontSize: '12px' }}>... and {data.length - 10} more items</div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (typeof data === 'object' && data !== null) {
    const keys = Object.keys(data);
    if (keys.length === 0) {
      return <span style={{ color: '#6b7280' }}>{'{}'}</span>;
    }

    return (
      <div style={{ marginLeft: level > 0 ? '16px' : '0' }}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            padding: '0',
            fontSize: '14px',
          }}
        >
          {isExpanded ? '▼' : '▶'} {name} ({keys.length} properties)
        </button>
        {isExpanded && (
          <div style={{ marginLeft: '16px' }}>
            {keys.slice(0, 20).map((key) => (
              <div key={key} style={{ marginBottom: '4px' }}>
                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>{key}: </span>
                <TreeView
                  data={(data as Record<string, unknown>)[key]}
                  name={key}
                  level={level + 1}
                  maxLevel={maxLevel}
                />
              </div>
            ))}
            {keys.length > 20 && (
              <div style={{ color: '#6b7280', fontSize: '12px' }}>
                ... and {keys.length - 20} more properties
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  try {
    return <span>{safeStringify(data)}</span>;
  } catch {
    return <span>[object]</span>;
  }
};

export const DebugDrawer: React.FC<DebugDrawerProps> = ({ isOpen, onClose }) => {
  const context = useTagadaContext();
  const pluginConfig = usePluginConfig();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [runningScripts, setRunningScripts] = useState<Set<string>>(new Set());
  const [scriptResults, setScriptResults] = useState<Record<string, { success: boolean; message: string }>>(
    {},
  );
  const [expandedScriptCategories, setExpandedScriptCategories] = useState<Record<string, boolean>>({});

  // Handler to jump to a specific step using direct_navigation
  const handleJumpToStep = async (stepId: string, stepName: string) => {
    // Try to get sessionId from debug data or context
    const sessionId = context.debugFunnel.data?.session?.sessionId || context.session?.sessionId;

    if (!sessionId) {
      alert('No active funnel session found');
      console.error('Debug data:', context.debugFunnel.data);
      console.error('Context:', context);
      return;
    }

    console.log(`🎯 [DebugDrawer] Jumping to step ${stepId} (${stepName}) in session ${sessionId}`);

    try {
      // Use direct_navigation event type to jump to any step
      const response = await context.apiService.fetch('/api/v1/funnel/navigate', {
        method: 'POST',
        body: {
          sessionId: sessionId,
          event: {
            type: 'direct_navigation',
            data: {
              targetStepId: stepId,
            },
            timestamp: new Date().toISOString(),
          },
          contextUpdates: {
            metadata: {
              debugJump: true,
              debugJumpFrom: typeof window !== 'undefined' ? window.location.href : undefined,
            },
          },
          currentUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        },
      });

      console.log('🎯 [DebugDrawer] Jump response:', response);

      if (response.success && response.result?.url) {
        // Navigate to the step URL
        console.log(`🎯 [DebugDrawer] Navigating to: ${response.result.url}`);
        window.location.href = response.result.url;
      } else {
        alert(`Failed to jump to ${stepName}: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to jump to step:', error);
      alert(`Failed to jump to ${stepName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const baseTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'config', label: 'Config' },
    { id: 'store', label: 'Store' },
    { id: 'session', label: 'Session' },
    { id: 'auth', label: 'Auth' },
  ];

  // Add funnel tab if funnel is active
  const funnelTab = context.debugFunnel.isActive ? [{ id: 'funnel', label: 'Funnel' }] : [];

  // Debug: Log comprehensive debug drawer state
  if (isOpen) {
    console.group('🐛 [DebugDrawer V2] Comprehensive State Check');
    console.log('1️⃣ Debug Mode:', context.debugMode);
    console.log('2️⃣ Funnel Debug State:', {
      isActive: context.debugFunnel.isActive,
      hasData: !!context.debugFunnel.data,
      hasError: !!context.debugFunnel.error,
      isLoading: context.debugFunnel.isLoading,
      lastUpdated: context.debugFunnel.lastUpdated,
    });
    console.log('3️⃣ Funnel Data (if any):', context.debugFunnel.data);
    console.log('4️⃣ Funnel Error (if any):', context.debugFunnel.error);
    console.log('5️⃣ Update Function Available:', typeof context.updateFunnelDebugData);
    console.log('6️⃣ Will show Funnel tab:', funnelTab.length > 0);
    console.groupEnd();
  }

  // Add checkout tab if checkout is active
  const checkoutTab = context.debugCheckout.isActive ? [{ id: 'checkout', label: 'Checkout' }] : [];

  // Add items tab if checkout has summary data
  const itemsTab =
    context.debugCheckout.isActive && context.debugCheckout.data?.checkout?.summary
      ? [{ id: 'items', label: 'Items' }]
      : [];

  // Add scripts tab if debug scripts are available
  const scriptsTab =
    context.debugScripts && context.debugScripts.length > 0
      ? [{ id: 'scripts', label: `Scripts (${context.debugScripts.length})` }]
      : [];

  const tabs = [
    ...baseTabs,
    ...funnelTab,
    ...checkoutTab,
    ...itemsTab,
    ...scriptsTab,
    { id: 'raw', label: 'Raw Data' },
  ];

  // Handler to run a debug script
  const handleRunScript = async (scriptId: string) => {
    const script = context.debugScripts?.find((s) => s.id === scriptId);
    if (!script) return;

    // Clear previous script results so only the current script shows a status
    setScriptResults({});

    setRunningScripts((prev) => new Set(prev).add(scriptId));

    try {
      await script.run(context);
      setScriptResults((prev) => ({
        ...prev,
        [scriptId]: { success: true, message: 'Script executed successfully' },
      }));
    } catch (error) {
      setScriptResults((prev) => ({
        ...prev,
        [scriptId]: {
          success: false,
          message: error instanceof Error ? error.message : 'Script failed',
        },
      }));
    } finally {
      setRunningScripts((prev) => {
        const next = new Set(prev);
        next.delete(scriptId);
        return next;
      });
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999998,
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '35vw',
          minWidth: '400px',
          maxWidth: '550px',
          backgroundColor: '#1f2937',
          color: '#f9fafb',
          boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.3)',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          fontSize: '13px',
          fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid #374151',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#111827',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>🐛 SDK Debug</h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
              Real-time state inspection
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '22px',
              padding: '2px',
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            padding: '0 12px',
            borderBottom: '1px solid #374151',
            backgroundColor: '#111827',
          }}
        >
          <div style={{ display: 'flex', gap: '0' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: activeTab === tab.id ? '#60a5fa' : '#9ca3af',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  fontSize: '12px',
                  borderBottom: activeTab === tab.id ? '2px solid #60a5fa' : '2px solid transparent',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: '12px',
            overflow: 'auto',
            backgroundColor: '#1f2937',
          }}
        >
          {activeTab === 'overview' && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '14px' }}>SDK Overview</h3>
              <div style={{ display: 'grid', gap: '10px', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Initialized:</span>
                  <span style={{ color: context.isInitialized ? '#10b981' : '#ef4444' }}>
                    {context.isInitialized ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Loading:</span>
                  <span style={{ color: context.isLoading ? '#f59e0b' : '#10b981' }}>
                    {context.isLoading ? '⏳ Yes' : '✅ No'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Environment:</span>
                  <span style={{ color: '#60a5fa' }}>
                    {context.environment.apiConfig.baseUrl.includes('dev') ? 'Development' : 'Production'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>API Base URL:</span>
                  <span style={{ color: '#10b981' }}>{context.environment.apiConfig.baseUrl}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Store ID:</span>
                  <span style={{ color: '#60a5fa' }}>{context.store?.id || 'Not set'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Customer ID:</span>
                  <span style={{ color: '#60a5fa' }}>{context.customer?.id || 'Not set'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Authenticated:</span>
                  <span style={{ color: context.auth.isAuthenticated ? '#10b981' : '#ef4444' }}>
                    {context.auth.isAuthenticated ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Currency:</span>
                  <span style={{ color: '#f59e0b' }}>
                    {context.currency.code} ({context.currency.symbol})
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Locale:</span>
                  <span style={{ color: '#8b5cf6' }}>{context.locale.locale}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '14px' }}>
                Plugin Configuration
              </h3>

              {/* Plugin Config Overview */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#9ca3af', fontSize: '13px' }}>
                  Configuration Summary
                </h4>
                <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Store ID:</span>
                    <span style={{ color: '#60a5fa' }}>{pluginConfig.storeId || 'Not set'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Account ID:</span>
                    <span style={{ color: '#60a5fa' }}>{pluginConfig.accountId || 'Not set'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Base Path:</span>
                    <span style={{ color: '#10b981' }}>{pluginConfig.basePath || '/'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Config Name:</span>
                    <span style={{ color: '#f59e0b' }}>
                      {(pluginConfig.config as any)?.configName || 'default'}
                    </span>
                  </div>
                  {pluginConfig.config?.branding?.primaryColor && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Primary Color:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: pluginConfig.config.branding.primaryColor,
                            border: '1px solid #374151',
                            borderRadius: '3px',
                          }}
                        />
                        <span style={{ color: '#10b981' }}>{pluginConfig.config.branding.primaryColor}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Full Config Data */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#9ca3af', fontSize: '14px' }}>
                  Full Configuration
                </h4>
                <TreeView data={pluginConfig} name="pluginConfig" />
              </div>

              {/* Local Config Info (Development Only) */}
              {typeof process !== 'undefined' && process.env?.NODE_ENV === 'development' && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    marginTop: '16px',
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', color: '#f59e0b', fontSize: '14px' }}>
                    Development Mode
                  </h4>
                  <p style={{ margin: '0', fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
                    Configuration is loaded from <code style={{ color: '#60a5fa' }}>/.local.json</code> and{' '}
                    <code style={{ color: '#60a5fa' }}>
                      /config/{(pluginConfig.config as any)?.configName || 'default'}.tgd.json
                    </code>
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#9ca3af', lineHeight: '1.4' }}>
                    Local config variant:{' '}
                    <code style={{ color: '#f59e0b' }}>
                      {(pluginConfig.config as any)?.configName || 'default'}
                    </code>
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'store' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Store Configuration</h3>
              {context.store ? (
                <TreeView data={context.store} name="store" />
              ) : (
                <p style={{ color: '#6b7280' }}>No store data available</p>
              )}
            </div>
          )}

          {activeTab === 'session' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Session Data</h3>
              {context.session ? (
                <TreeView data={context.session} name="session" />
              ) : (
                <p style={{ color: '#6b7280' }}>No session data available</p>
              )}
            </div>
          )}

          {activeTab === 'auth' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Authentication State</h3>
              <TreeView data={context.auth} name="auth" />

              {context.customer && (
                <>
                  <h4 style={{ margin: '24px 0 12px 0', color: '#60a5fa' }}>Customer Data</h4>
                  <TreeView data={context.customer} name="customer" />
                </>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Items & Discounts</h3>
              {context.debugCheckout.data?.checkout?.summary ? (
                <div>
                  {/* Summary Overview */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>Summary</h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Currency:</span>
                        <span style={{ color: '#f59e0b' }}>
                          {context.debugCheckout.data.checkout.summary.currency}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Subtotal:</span>
                        <span style={{ color: '#9ca3af' }}>
                          {(() => {
                            try {
                              return context.money.formatMoney(
                                Number(context.debugCheckout.data.checkout.summary.subtotalAmount) || 0,
                                String(context.debugCheckout.data.checkout.summary.currency) || 'USD',
                                context.locale.locale,
                              );
                            } catch {
                              return String(context.debugCheckout.data.checkout.summary.subtotalAmount);
                            }
                          })()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Promotions:</span>
                        <span style={{ color: '#ef4444' }}>
                          {(() => {
                            try {
                              return `-${context.money.formatMoney(
                                Number(context.debugCheckout.data.checkout.summary.totalPromotionAmount) || 0,
                                String(context.debugCheckout.data.checkout.summary.currency) || 'USD',
                                context.locale.locale,
                              )}`;
                            } catch {
                              return `-${String(context.debugCheckout.data.checkout.summary.totalPromotionAmount)}`;
                            }
                          })()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Adjusted:</span>
                        <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                          {(() => {
                            try {
                              return context.money.formatMoney(
                                Number(context.debugCheckout.data.checkout.summary.totalAdjustedAmount) || 0,
                                String(context.debugCheckout.data.checkout.summary.currency) || 'USD',
                                context.locale.locale,
                              );
                            } catch {
                              return String(context.debugCheckout.data.checkout.summary.totalAdjustedAmount);
                            }
                          })()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Shipping:</span>
                        <span style={{ color: '#9ca3af' }}>
                          {(() => {
                            try {
                              const shipping =
                                Number(context.debugCheckout.data.checkout.summary.shippingCost) || 0;
                              return shipping === 0
                                ? 'FREE'
                                : context.money.formatMoney(
                                    shipping,
                                    String(context.debugCheckout.data.checkout.summary.currency) || 'USD',
                                    context.locale.locale,
                                  );
                            } catch {
                              return String(context.debugCheckout.data.checkout.summary.shippingCost);
                            }
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>
                      Line Items ({context.debugCheckout.data.checkout.summary.items.length})
                    </h4>
                    <div style={{ display: 'grid', gap: '16px' }}>
                      {context.debugCheckout.data.checkout.summary.items.map((item: any, index: number) => (
                        <div
                          key={item.id || index}
                          style={{
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            padding: '12px',
                            backgroundColor: '#111827',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            {/* Product Image - check multiple possible sources */}
                            {(() => {
                              const imageUrl =
                                item.variant?.imageUrl ||
                                item.orderLineItemVariant?.imageUrl ||
                                item.imageUrl ||
                                item.product?.image?.src ||
                                item.product?.images?.[0]?.src;

                              return imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={
                                    item.product?.name ||
                                    item.orderLineItemProduct?.name ||
                                    item.name ||
                                    'Product'
                                  }
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    backgroundColor: '#374151',
                                  }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: '#374151',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px',
                                    color: '#9ca3af',
                                  }}
                                >
                                  📦
                                </div>
                              );
                            })()}

                            {/* Product Details */}
                            <div style={{ flex: 1 }}>
                              <div style={{ marginBottom: '8px' }}>
                                <h5
                                  style={{
                                    margin: '0 0 4px 0',
                                    color: '#f9fafb',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {item.product?.name ||
                                    item.orderLineItemProduct?.name ||
                                    item.name ||
                                    'Unknown Product'}
                                  {item.isOrderBump && (
                                    <span
                                      style={{
                                        marginLeft: '8px',
                                        fontSize: '11px',
                                        backgroundColor: '#f59e0b',
                                        color: '#000',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      ORDER BUMP
                                    </span>
                                  )}
                                </h5>
                                {/* Variant Information */}
                                {(item.variant?.name || item.orderLineItemVariant?.name) && (
                                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#9ca3af' }}>
                                    Variant: {item.variant?.name || item.orderLineItemVariant?.name}
                                  </p>
                                )}
                                {/* Product Description */}
                                {(item.product?.description || item.description) && (
                                  <p style={{ margin: '0', fontSize: '11px', color: '#6b7280' }}>
                                    {(() => {
                                      const desc = item.product?.description || item.description;
                                      // Strip HTML tags for display
                                      const cleaned = desc.replace(/<[^>]*>/g, '');
                                      return cleaned.length > 100
                                        ? cleaned.substring(0, 100) + '...'
                                        : cleaned;
                                    })()}
                                  </p>
                                )}
                              </div>

                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: '8px',
                                  fontSize: '12px',
                                  marginBottom: '8px',
                                }}
                              >
                                <div>
                                  <span style={{ color: '#9ca3af' }}>Quantity: </span>
                                  <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                                    {item.quantity}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: '#9ca3af' }}>Unit Price: </span>
                                  <span style={{ color: '#f9fafb' }}>
                                    {(() => {
                                      try {
                                        return context.money.formatMoney(
                                          Number(item.unitAmount) || 0,
                                          String(context.debugCheckout.data.checkout.summary.currency) ||
                                            'USD',
                                          context.locale.locale,
                                        );
                                      } catch {
                                        return String(item.unitAmount);
                                      }
                                    })()}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: '#9ca3af' }}>Original: </span>
                                  <span
                                    style={{
                                      color: item.amount !== item.adjustedAmount ? '#9ca3af' : '#f9fafb',
                                      textDecoration:
                                        item.amount !== item.adjustedAmount ? 'line-through' : 'none',
                                    }}
                                  >
                                    {(() => {
                                      try {
                                        return context.money.formatMoney(
                                          Number(item.amount) || 0,
                                          String(context.debugCheckout.data.checkout.summary.currency) ||
                                            'USD',
                                          context.locale.locale,
                                        );
                                      } catch {
                                        return String(item.amount);
                                      }
                                    })()}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: '#9ca3af' }}>Final: </span>
                                  <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                    {(() => {
                                      try {
                                        return context.money.formatMoney(
                                          Number(item.adjustedAmount) || 0,
                                          String(context.debugCheckout.data.checkout.summary.currency) ||
                                            'USD',
                                          context.locale.locale,
                                        );
                                      } catch {
                                        return String(item.adjustedAmount);
                                      }
                                    })()}
                                  </span>
                                  {item.amount !== item.adjustedAmount && (
                                    <span style={{ color: '#ef4444', marginLeft: '4px' }}>
                                      (
                                      {(() => {
                                        try {
                                          const discount = Number(item.amount) - Number(item.adjustedAmount);
                                          return `-${context.money.formatMoney(
                                            discount,
                                            String(context.debugCheckout.data.checkout.summary.currency) ||
                                              'USD',
                                            context.locale.locale,
                                          )}`;
                                        } catch {
                                          return `-${item.amount - item.adjustedAmount}`;
                                        }
                                      })()}
                                      )
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Item-level adjustments/promotions */}
                              {item.adjustments && item.adjustments.length > 0 && (
                                <div
                                  style={{
                                    backgroundColor: '#065f46',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    marginTop: '8px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      marginBottom: '4px',
                                      color: '#6ee7b7',
                                    }}
                                  >
                                    💰 Item Promotions Applied:
                                  </div>
                                  {item.adjustments.map((adjustment: any, adjIndex: number) => (
                                    <div key={adjIndex} style={{ fontSize: '10px', marginBottom: '2px' }}>
                                      <div style={{ color: '#6ee7b7', fontWeight: 'bold' }}>
                                        {adjustment.description}
                                      </div>
                                      <div style={{ color: '#a7f3d0' }}>
                                        Amount:{' '}
                                        {(() => {
                                          try {
                                            return context.money.formatMoney(
                                              Math.abs(Number(adjustment.amount) || 0),
                                              String(adjustment.currency) ||
                                                String(
                                                  context.debugCheckout.data.checkout.summary.currency,
                                                ) ||
                                                'USD',
                                              context.locale.locale,
                                            );
                                          } catch {
                                            return String(Math.abs(Number(adjustment.amount) || 0));
                                          }
                                        })()}{' '}
                                        • Type: {adjustment.type} • Level: {adjustment.level}
                                        {adjustment.sourceId && ` • ID: ${adjustment.sourceId}`}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Product IDs for debugging */}
                              <div style={{ fontSize: '9px', color: '#6b7280', marginTop: '8px' }}>
                                Product ID: {item.productId} • Variant ID: {item.variantId} • Price ID:{' '}
                                {item.priceId}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Available Promotions */}
                  {context.debugCheckout.data?.availablePromotions &&
                    context.debugCheckout.data.availablePromotions.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>
                          Available Promotions ({context.debugCheckout.data.availablePromotions.length})
                        </h4>
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {context.debugCheckout.data.availablePromotions.map(
                            (promotion: any, index: number) => (
                              <div
                                key={promotion.id || index}
                                style={{
                                  border: '1px solid #374151',
                                  borderRadius: '6px',
                                  padding: '12px',
                                  backgroundColor: '#111827',
                                }}
                              >
                                <div style={{ marginBottom: '8px' }}>
                                  <div style={{ color: '#f9fafb', fontWeight: 'bold', marginBottom: '4px' }}>
                                    {promotion.name}
                                    {promotion.automatic && (
                                      <span
                                        style={{
                                          marginLeft: '8px',
                                          fontSize: '10px',
                                          backgroundColor: '#10b981',
                                          color: '#000',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                        }}
                                      >
                                        AUTO
                                      </span>
                                    )}
                                    {promotion.enabled && (
                                      <span
                                        style={{
                                          marginLeft: '8px',
                                          fontSize: '10px',
                                          backgroundColor: '#3b82f6',
                                          color: '#fff',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                        }}
                                      >
                                        ACTIVE
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    ID: {promotion.id} • Usage: {promotion.usageCount || 0}
                                    {promotion.usageLimit && ` / ${promotion.usageLimit}`}
                                  </div>
                                </div>

                                {/* Promotion Rules */}
                                {promotion.rules && promotion.rules.length > 0 && (
                                  <div style={{ marginBottom: '8px' }}>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#60a5fa',
                                        fontWeight: 'bold',
                                        marginBottom: '4px',
                                      }}
                                    >
                                      Rules:
                                    </div>
                                    {promotion.rules.map((rule: any, ruleIndex: number) => (
                                      <div
                                        key={ruleIndex}
                                        style={{ fontSize: '9px', color: '#d1d5db', marginBottom: '2px' }}
                                      >
                                        • {rule.type} {rule.productId && `(Product: ${rule.productId})`}
                                        {rule.minimumQuantity && ` - Min Qty: ${rule.minimumQuantity}`}
                                        {rule.minimumAmount && ` - Min Amount: ${rule.minimumAmount}`}
                                        {rule.variantIds &&
                                          rule.variantIds.length > 0 &&
                                          ` - Variants: ${rule.variantIds.length}`}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Promotion Actions */}
                                {promotion.actions && promotion.actions.length > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontSize: '10px',
                                        color: '#10b981',
                                        fontWeight: 'bold',
                                        marginBottom: '4px',
                                      }}
                                    >
                                      Actions:
                                    </div>
                                    {promotion.actions.map((action: any, actionIndex: number) => (
                                      <div
                                        key={actionIndex}
                                        style={{ fontSize: '9px', color: '#d1d5db', marginBottom: '2px' }}
                                      >
                                        • {action.type}
                                        {action.adjustmentPercentage &&
                                          ` - ${action.adjustmentPercentage}% off`}
                                        {action.adjustmentAmount && ` - ${action.adjustmentAmount} off`}
                                        {action.freeShipping && ` - Free Shipping`}
                                        {action.targetProductId && ` - Target: ${action.targetProductId}`}
                                        {action.maxQuantityDiscounted &&
                                          ` - Max Qty: ${action.maxQuantityDiscounted}`}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Order Level Discounts */}
                  {context.debugCheckout.data.checkout.summary.adjustments &&
                    context.debugCheckout.data.checkout.summary.adjustments.length > 0 && (
                      <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>
                          Order Level Discounts (
                          {context.debugCheckout.data.checkout.summary.adjustments.length})
                        </h4>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {context.debugCheckout.data.checkout.summary.adjustments.map(
                            (adjustment: any, index: number) => (
                              <div
                                key={index}
                                style={{
                                  border: '1px solid #374151',
                                  borderRadius: '6px',
                                  padding: '10px',
                                  backgroundColor: '#111827',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <div>
                                  <div style={{ color: '#f9fafb', fontWeight: 'bold', marginBottom: '2px' }}>
                                    {adjustment.description}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {adjustment.type} • Level: {adjustment.level}
                                    {adjustment.sourceId && ` • ID: ${adjustment.sourceId}`}
                                  </div>
                                </div>
                                <div style={{ color: '#ef4444', fontWeight: 'bold' }}>
                                  {(() => {
                                    try {
                                      return context.money.formatMoney(
                                        Math.abs(Number(adjustment.amount)) || 0,
                                        String(adjustment.currency) ||
                                          String(context.debugCheckout.data.checkout.summary.currency) ||
                                          'USD',
                                        context.locale.locale,
                                      );
                                    } catch {
                                      return String(Math.abs(Number(adjustment.amount)));
                                    }
                                  })()}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No checkout summary data available</p>
              )}
            </div>
          )}

          {activeTab === 'funnel' && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '14px' }}>🚀 Funnel Debug</h3>
              {context.debugFunnel.isActive ? (
                <div>
                  {/* Compact Status Bar */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      padding: '6px 10px',
                      backgroundColor: '#111827',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      fontSize: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: '#10b981' }}>✅ Active</span>
                    <span style={{ color: '#4b5563' }}>•</span>
                    <span style={{ color: context.debugFunnel.isLoading ? '#f59e0b' : '#6b7280' }}>
                      {context.debugFunnel.isLoading ? '⏳ Loading' : '✓ Ready'}
                    </span>
                    {context.debugFunnel.error && (
                      <>
                        <span style={{ color: '#4b5563' }}>•</span>
                        <span style={{ color: '#ef4444' }}>❌ Error</span>
                      </>
                    )}
                    <span style={{ color: '#4b5563', marginLeft: 'auto' }}>
                      {context.debugFunnel.lastUpdated?.toLocaleTimeString() || 'Never'}
                    </span>
                  </div>

                  {/* Error Display */}
                  {context.debugFunnel.error && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#ef4444' }}>Error Details</h4>
                      <div
                        style={{
                          backgroundColor: '#372c2c',
                          padding: '12px',
                          borderRadius: '4px',
                          border: '1px solid #ef4444',
                        }}
                      >
                        <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '8px' }}>
                          {context.debugFunnel.error.name}: {context.debugFunnel.error.message}
                        </div>
                        {context.debugFunnel.error.stack && (
                          <pre
                            style={{
                              color: '#9ca3af',
                              fontSize: '11px',
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {context.debugFunnel.error.stack}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Compact Funnel & Session Info */}
                  {context.debugFunnel.data?.funnel && (
                    <div
                      style={{
                        padding: '8px 10px',
                        backgroundColor: '#111827',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        fontSize: '11px',
                      }}
                    >
                      {/* Funnel Info */}
                      <div style={{ marginBottom: '10px' }}>
                        <div
                          style={{
                            color: '#60a5fa',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            marginBottom: '6px',
                          }}
                        >
                          📊 {context.debugFunnel.data.funnel.name}
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '6px',
                            fontSize: '11px',
                          }}
                        >
                          <div>
                            <span style={{ color: '#6b7280' }}>ID: </span>
                            <span style={{ color: '#f59e0b', fontFamily: 'monospace', fontSize: '10px' }}>
                              {context.debugFunnel.data.funnel.id}
                            </span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ color: '#6b7280' }}>Steps: </span>
                            <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                              {context.debugFunnel.data.funnel.steps?.length || 0}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Session Info */}
                      {context.debugFunnel.data?.session && (
                        <div
                          style={{
                            paddingTop: '10px',
                            borderTop: '1px solid #374151',
                          }}
                        >
                          <div
                            style={{
                              color: '#60a5fa',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              marginBottom: '6px',
                            }}
                          >
                            🎯 Session
                          </div>
                          <div style={{ display: 'grid', gap: '4px', fontSize: '11px' }}>
                            {context.debugFunnel.data.session.sessionId && (
                              <div style={{ marginBottom: '6px' }}>
                                <span style={{ color: '#6b7280' }}>ID: </span>
                                <span
                                  style={{
                                    color: '#f59e0b',
                                    fontFamily: 'monospace',
                                    fontSize: '10px',
                                  }}
                                >
                                  {context.debugFunnel.data.session.sessionId}
                                </span>
                              </div>
                            )}
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span style={{ color: '#6b7280' }}>Current:</span>
                              <span
                                style={{
                                  color: '#10b981',
                                  fontWeight: 'bold',
                                  fontFamily: 'monospace',
                                  fontSize: '10px',
                                  backgroundColor: '#065f46',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                }}
                              >
                                {context.debugFunnel.data.session.currentStepId}
                              </span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                              <div>
                                <span style={{ color: '#6b7280' }}>Furthest: </span>
                                <span style={{ color: '#60a5fa', fontSize: '10px' }}>
                                  {context.debugFunnel.data.session.furthestStepId}
                                </span>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{ color: '#6b7280' }}>Previous: </span>
                                <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                                  {context.debugFunnel.data.session.previousStepId || 'None'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Funnel Steps */}
                  {context.debugFunnel.data?.funnel?.steps && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#60a5fa', fontSize: '13px' }}>
                        📋 Steps ({context.debugFunnel.data.funnel.steps.length})
                      </h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {context.debugFunnel.data.funnel.steps.map((step: any, index: number) => {
                          const isCurrent = step.id === context.debugFunnel.data?.session?.currentStepId;
                          const isVisited = context.debugFunnel.data?.session?.visitedSteps?.includes(
                            step.id,
                          );
                          const isEntry = step.id === context.debugFunnel.data?.funnel?.entryStepId;

                          return (
                            <div
                              key={step.id}
                              style={{
                                border: isCurrent ? '2px solid #10b981' : '1px solid #374151',
                                borderRadius: '6px',
                                padding: '8px 10px',
                                backgroundColor: isCurrent ? '#065f46' : '#111827',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginBottom: '4px',
                                }}
                              >
                                <span
                                  style={{
                                    color: isCurrent ? '#fff' : '#9ca3af',
                                    fontSize: '10px',
                                    minWidth: '18px',
                                  }}
                                >
                                  {index + 1}.
                                </span>
                                <span
                                  style={{
                                    color: isCurrent ? '#fff' : '#f9fafb',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    flex: 1,
                                  }}
                                >
                                  {step.name}
                                </span>
                                {isCurrent && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      backgroundColor: '#10b981',
                                      color: '#000',
                                      padding: '2px 5px',
                                      borderRadius: '3px',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    CURRENT
                                  </span>
                                )}
                                {isEntry && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      backgroundColor: '#8b5cf6',
                                      color: '#fff',
                                      padding: '2px 5px',
                                      borderRadius: '3px',
                                    }}
                                  >
                                    ENTRY
                                  </span>
                                )}
                                {isVisited && !isCurrent && (
                                  <span style={{ fontSize: '12px', color: '#10b981' }}>✓</span>
                                )}
                                {!isCurrent && (
                                  <button
                                    onClick={() => handleJumpToStep(step.id, step.name)}
                                    style={{
                                      fontSize: '9px',
                                      backgroundColor: '#3b82f6',
                                      color: '#fff',
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontWeight: 'bold',
                                      transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#2563eb';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#3b82f6';
                                    }}
                                    title={`Jump to ${step.name}`}
                                  >
                                    ↗ JUMP
                                  </button>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: '10px',
                                  color: isCurrent ? '#d1fae5' : '#9ca3af',
                                  fontFamily: 'monospace',
                                  marginBottom: '2px',
                                  marginLeft: '24px',
                                }}
                              >
                                {step.id}
                              </div>
                              <div
                                style={{
                                  fontSize: '10px',
                                  color: isCurrent ? '#a7f3d0' : '#6b7280',
                                  marginLeft: '24px',
                                }}
                              >
                                {step.type}
                              </div>

                              {/* Step URLs */}
                              {step.urls && step.urls.length > 0 && (
                                <div style={{ marginTop: '6px', marginLeft: '24px' }}>
                                  <div
                                    style={{
                                      fontSize: '9px',
                                      color: isCurrent ? '#a7f3d0' : '#9ca3af',
                                      marginBottom: '2px',
                                    }}
                                  >
                                    🔗 {step.urls.length} URL{step.urls.length > 1 ? 's' : ''}
                                  </div>
                                  {step.urls.map((url: string, urlIdx: number) => (
                                    <div
                                      key={urlIdx}
                                      style={{
                                        fontSize: '9px',
                                        color: isCurrent ? '#6ee7b7' : '#6b7280',
                                        fontFamily: 'monospace',
                                        marginLeft: '12px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      • {url}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Step Conditions */}
                              {step.conditions && step.conditions.length > 0 && (
                                <div
                                  style={{
                                    marginTop: '6px',
                                    padding: '6px',
                                    backgroundColor: isCurrent ? '#047857' : '#1f2937',
                                    borderRadius: '4px',
                                    marginLeft: '24px',
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: '9px',
                                      color: '#f59e0b',
                                      fontWeight: 'bold',
                                      marginBottom: '3px',
                                    }}
                                  >
                                    ⚙️ {step.conditions.length} Condition
                                    {step.conditions.length > 1 ? 's' : ''}
                                  </div>
                                  {step.conditions.map((cond: any, condIdx: number) => (
                                    <div
                                      key={condIdx}
                                      style={{
                                        fontSize: '9px',
                                        color: isCurrent ? '#d1fae5' : '#9ca3af',
                                        marginLeft: '8px',
                                      }}
                                    >
                                      • {cond.type} → {cond.nextStepId}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Context & Resources */}
                  {context.debugFunnel.data?.session && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#60a5fa', fontSize: '13px' }}>
                        📦 Resources & Context
                      </h4>

                      {/* Resources Section */}
                      {context.debugFunnel.data.session.resources &&
                        Object.keys(context.debugFunnel.data.session.resources).length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div
                              style={{
                                fontSize: '11px',
                                color: '#10b981',
                                fontWeight: 'bold',
                                marginBottom: '6px',
                              }}
                            >
                              🎯 Available Resources (
                              {Object.keys(context.debugFunnel.data.session.resources).length})
                            </div>
                            <div style={{ display: 'grid', gap: '8px' }}>
                              {Object.entries(context.debugFunnel.data.session.resources).map(
                                ([key, value]: [string, any]) => {
                                  // Skip undefined/null resources
                                  if (value === undefined || value === null) return null;

                                  // Check if it's an array or single resource
                                  const isArray = Array.isArray(value);
                                  const resourceCount = isArray ? value.length : 1;

                                  return (
                                    <div
                                      key={key}
                                      style={{
                                        border: '1px solid #374151',
                                        borderRadius: '6px',
                                        padding: '10px',
                                        backgroundColor: '#111827',
                                      }}
                                    >
                                      {/* Resource Header */}
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: '8px',
                                        }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <span style={{ color: '#10b981', fontSize: '14px' }}>
                                            {key === 'order' || key === 'mainOrder'
                                              ? '🛒'
                                              : key === 'customer'
                                                ? '👤'
                                                : key === 'checkout'
                                                  ? '💳'
                                                  : key === 'offer'
                                                    ? '🎁'
                                                    : key === 'subscription'
                                                      ? '🔄'
                                                      : '📄'}
                                          </span>
                                          <span
                                            style={{
                                              color: '#f9fafb',
                                              fontWeight: 'bold',
                                              fontSize: '12px',
                                            }}
                                          >
                                            {key}
                                          </span>
                                          {isArray && (
                                            <span
                                              style={{
                                                fontSize: '10px',
                                                backgroundColor: '#3b82f6',
                                                color: '#fff',
                                                padding: '2px 6px',
                                                borderRadius: '3px',
                                              }}
                                            >
                                              {resourceCount} items
                                            </span>
                                          )}
                                        </div>

                                        {/* Quick Info */}
                                        {!isArray && value && typeof value === 'object' && (
                                          <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                                            {value.id && (
                                              <span style={{ fontFamily: 'monospace' }}>{value.id}</span>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Resource Details */}
                                      {!isArray && value && typeof value === 'object' && (
                                        <div
                                          style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '6px',
                                            fontSize: '11px',
                                            marginBottom: '8px',
                                          }}
                                        >
                                          {/* Show key properties based on resource type */}
                                          {value.amount !== undefined && (
                                            <div>
                                              <span style={{ color: '#6b7280' }}>Amount: </span>
                                              <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                                {(() => {
                                                  try {
                                                    return context.money.formatMoney(
                                                      Number(value.amount) || 0,
                                                      String(value.currency) || 'USD',
                                                      context.locale.locale,
                                                    );
                                                  } catch {
                                                    return `${value.amount} ${value.currency || ''}`;
                                                  }
                                                })()}
                                              </span>
                                            </div>
                                          )}
                                          {value.email !== undefined && (
                                            <div>
                                              <span style={{ color: '#6b7280' }}>Email: </span>
                                              <span style={{ color: '#60a5fa' }}>{value.email}</span>
                                            </div>
                                          )}
                                          {value.status !== undefined && (
                                            <div>
                                              <span style={{ color: '#6b7280' }}>Status: </span>
                                              <span style={{ color: '#f59e0b' }}>{value.status}</span>
                                            </div>
                                          )}
                                          {value.currency !== undefined && (
                                            <div>
                                              <span style={{ color: '#6b7280' }}>Currency: </span>
                                              <span style={{ color: '#8b5cf6' }}>{value.currency}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Full Data Expandable */}
                                      <details style={{ marginTop: '6px' }}>
                                        <summary
                                          style={{
                                            cursor: 'pointer',
                                            fontSize: '10px',
                                            color: '#60a5fa',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          Show Full Data
                                        </summary>
                                        <div style={{ marginTop: '6px', fontSize: '10px' }}>
                                          <TreeView data={value} name={key} maxLevel={3} />
                                        </div>
                                      </details>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}

                      {/* Metadata Section */}
                      {context.debugFunnel.data.session.metadata &&
                        Object.keys(context.debugFunnel.data.session.metadata).length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div
                              style={{
                                fontSize: '11px',
                                color: '#8b5cf6',
                                fontWeight: 'bold',
                                marginBottom: '6px',
                              }}
                            >
                              📝 Session Metadata (
                              {Object.keys(context.debugFunnel.data.session.metadata).length})
                            </div>
                            <div
                              style={{
                                border: '1px solid #374151',
                                borderRadius: '6px',
                                padding: '10px',
                                backgroundColor: '#111827',
                              }}
                            >
                              <TreeView
                                data={context.debugFunnel.data.session.metadata}
                                name="metadata"
                                maxLevel={2}
                              />
                            </div>
                          </div>
                        )}

                      {/* Context Info */}
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#f59e0b',
                            fontWeight: 'bold',
                            marginBottom: '6px',
                          }}
                        >
                          ℹ️ Session Context
                        </div>
                        <div
                          style={{
                            border: '1px solid #374151',
                            borderRadius: '6px',
                            padding: '10px',
                            backgroundColor: '#111827',
                          }}
                        >
                          <div style={{ display: 'grid', gap: '6px', fontSize: '11px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Customer ID:</span>
                              <span style={{ color: '#60a5fa', fontFamily: 'monospace', fontSize: '10px' }}>
                                {context.debugFunnel.data.session.customerId}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#6b7280' }}>Store ID:</span>
                              <span style={{ color: '#60a5fa', fontFamily: 'monospace', fontSize: '10px' }}>
                                {context.debugFunnel.data.session.storeId}
                              </span>
                            </div>
                            {context.debugFunnel.data.session.environment && (
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Environment:</span>
                                <span style={{ color: '#10b981' }}>
                                  {context.debugFunnel.data.session.environment}
                                </span>
                              </div>
                            )}
                            {context.debugFunnel.data.session.startedAt && (
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Started At:</span>
                                <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                                  {new Date(context.debugFunnel.data.session.startedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {context.debugFunnel.data.session.lastActivityAt && (
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Last Activity:</span>
                                <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                                  {new Date(context.debugFunnel.data.session.lastActivityAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Full Funnel Data (Collapsed by Default) */}
                  <details style={{ marginTop: '12px' }}>
                    <summary
                      style={{
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        color: '#60a5fa',
                        marginBottom: '8px',
                      }}
                    >
                      🔍 Raw Funnel Data (Debug)
                    </summary>
                    <div style={{ fontSize: '11px', marginTop: '8px' }}>
                      <TreeView data={context.debugFunnel.data} name="funnelData" maxLevel={4} />
                    </div>
                  </details>
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No funnel hook active</p>
              )}
            </div>
          )}

          {activeTab === 'checkout' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Checkout Debug</h3>
              {context.debugCheckout.isActive ? (
                <div>
                  {/* Checkout Status Overview */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>Status</h4>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Hook Active:</span>
                        <span style={{ color: '#10b981' }}>✅ Yes</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Loading:</span>
                        <span style={{ color: context.debugCheckout.isLoading ? '#f59e0b' : '#10b981' }}>
                          {context.debugCheckout.isLoading ? '⏳ Yes' : '✅ No'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Has Error:</span>
                        <span style={{ color: context.debugCheckout.error ? '#ef4444' : '#10b981' }}>
                          {context.debugCheckout.error ? '❌ Yes' : '✅ No'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Last Updated:</span>
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                          {context.debugCheckout.lastUpdated?.toLocaleTimeString() || 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Error Display */}
                  {context.debugCheckout.error && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#ef4444' }}>Error Details</h4>
                      <div
                        style={{
                          backgroundColor: '#372c2c',
                          padding: '12px',
                          borderRadius: '4px',
                          border: '1px solid #ef4444',
                        }}
                      >
                        <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '8px' }}>
                          {context.debugCheckout.error.name}: {context.debugCheckout.error.message}
                        </div>
                        {context.debugCheckout.error.stack && (
                          <pre
                            style={{
                              color: '#9ca3af',
                              fontSize: '11px',
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {context.debugCheckout.error.stack}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Checkout Data */}
                  <div>
                    <h4 style={{ margin: '0 0 12px 0', color: '#60a5fa' }}>Full Checkout Data</h4>
                    <TreeView data={context.debugCheckout.data} name="checkoutData" />
                  </div>
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No checkout hook active</p>
              )}
            </div>
          )}

          {activeTab === 'scripts' && (
            <div>
              <h3 style={{ margin: '0 0 12px 0', color: '#60a5fa', fontSize: '14px' }}>🛠️ Debug Scripts</h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '11px', color: '#9ca3af' }}>
                Template-provided scripts for debugging and testing. Click a script to run it.
              </p>
              {context.debugScripts && context.debugScripts.length > 0 ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {/* Group scripts by category */}
                  {(() => {
                    const grouped = context.debugScripts.reduce(
                      (acc, script) => {
                        const category = script.category || 'General';
                        if (!acc[category]) acc[category] = [];
                        acc[category].push(script);
                        return acc;
                      },
                      {} as Record<string, typeof context.debugScripts>,
                    );

                    return Object.entries(grouped).map(([category, scripts]) => {
                      const isExpanded = expandedScriptCategories[category] ?? false;

                      const toggleCategory = () => {
                        setExpandedScriptCategories((prev) => ({
                          ...prev,
                          [category]: !(prev[category] ?? false),
                        }));
                      };

                      return (
                        <div key={category}>
                          <button
                            type="button"
                            onClick={toggleCategory}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              background: 'none',
                              border: 'none',
                              padding: '6px 0',
                              cursor: 'pointer',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#9ca3af',
                              }}
                            >
                              <span style={{ fontSize: '10px' }}>{isExpanded ? '▼' : '▶'}</span>
                              <span>{category}</span>
                              <span style={{ fontSize: '10px', color: '#6b7280' }}>({scripts.length})</span>
                            </div>
                          </button>

                          {isExpanded && (
                            <div style={{ display: 'grid', gap: '8px', marginTop: '4px' }}>
                              {scripts.map((script) => {
                                const isRunning = runningScripts.has(script.id);
                                const result = scriptResults[script.id];

                                return (
                                  <div
                                    key={script.id}
                                    style={{
                                      border: '1px solid #374151',
                                      borderRadius: '6px',
                                      padding: '10px 12px',
                                      backgroundColor: '#111827',
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: '12px',
                                      }}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <div
                                          style={{
                                            color: '#f9fafb',
                                            fontWeight: 'bold',
                                            fontSize: '12px',
                                            marginBottom: '4px',
                                          }}
                                        >
                                          {script.name}
                                        </div>
                                        {script.description && (
                                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                            {script.description}
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => handleRunScript(script.id)}
                                        disabled={isRunning}
                                        style={{
                                          backgroundColor: isRunning ? '#374151' : '#3b82f6',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '4px',
                                          padding: '6px 12px',
                                          fontSize: '11px',
                                          fontWeight: 'bold',
                                          cursor: isRunning ? 'not-allowed' : 'pointer',
                                          transition: 'background-color 0.2s',
                                          minWidth: '60px',
                                        }}
                                      >
                                        {isRunning ? '⏳' : '▶ Run'}
                                      </button>
                                    </div>

                                    {/* Result display */}
                                    {result && (
                                      <div
                                        style={{
                                          marginTop: '8px',
                                          padding: '6px 8px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          backgroundColor: result.success ? '#065f46' : '#7f1d1d',
                                          color: result.success ? '#6ee7b7' : '#fca5a5',
                                        }}
                                      >
                                        {result.success ? '✅' : '❌'} {result.message}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No debug scripts available</p>
              )}
            </div>
          )}

          {activeTab === 'raw' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', color: '#60a5fa' }}>Raw Context Data</h3>
              <div style={{ fontSize: '12px' }}>
                <TreeView data={context} name="tagadaContext" maxLevel={4} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DebugDrawer;
